import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PriceMaskingContextType {
  isPriceMasked: boolean;
  togglePriceMasking: () => void;
  setPriceMasking: (masked: boolean) => void;
}

const PriceMaskingContext = createContext<PriceMaskingContextType | undefined>(undefined);

const PRICE_MASKING_STORAGE_KEY = 'price_masking_enabled';

export const PriceMaskingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isPriceMasked, setIsPriceMasked] = useState<boolean>(() => {
    // Load from localStorage on initialization
    const saved = localStorage.getItem(PRICE_MASKING_STORAGE_KEY);
    return saved === 'true';
  });

  useEffect(() => {
    // Save to localStorage whenever it changes
    localStorage.setItem(PRICE_MASKING_STORAGE_KEY, String(isPriceMasked));
  }, [isPriceMasked]);

  const togglePriceMasking = () => {
    setIsPriceMasked((prev) => !prev);
  };

  const setPriceMasking = (masked: boolean) => {
    setIsPriceMasked(masked);
  };

  return (
    <PriceMaskingContext.Provider
      value={{
        isPriceMasked,
        togglePriceMasking,
        setPriceMasking,
      }}
    >
      {children}
    </PriceMaskingContext.Provider>
  );
};

export const usePriceMasking = (): PriceMaskingContextType => {
  const context = useContext(PriceMaskingContext);
  if (context === undefined) {
    throw new Error('usePriceMasking must be used within a PriceMaskingProvider');
  }
  return context;
};
